import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "@remix-run/react";
import { Description, DetailsContainer, ImageContainer, InfoContainer, MediaTypeOverlay, MetaInformation, Overlay, SearchTeaserLabel, ShowTitle, StyledMediaSearchResult, Title } from './MediaSearchResult.styles';
import { MediaTypeIcon } from '../../../../../play-core/icons/MediaTypeIcon';
import { leadOrDescription } from '../../../../../play-core/utils/string-helper';
import { useAnalyticsDispatch } from '../../../analytics/AnalyticsContext';
import { Download } from '../../../components/ClickableComponents/Download';
import { colorSets } from '../../../components/ClickableComponents/styles';
import { DotSeparator } from '../../../components/DotSeparator/DotSeparator';
import { IconWrapper } from '../../../components/IconWrapper/IconWrapper';
import { PlayImage } from '../../../components/Image/PlayImage';
import { getExpiringSoonLabelProps } from '../../../components/InfoLabel/label-helper';
import { MetaInformationContainer } from '../../../components/Teaser/TeaserActions/ImageOverlay.styles';
import { MediaDuration } from '../../../components/Teaser/TeaserActions/MediaDuration';
import { SubtitleLabel } from '../../../components/Teaser/TeaserActions/MediaSubtitles';
import { AbroadOverlay } from '../../../components/Teaser/TeaserOverlayInfo/AbroadOverlay';
import { usePath, useRoutes } from '../../../config/VideoAppContext';
import { useHydrated } from '../../../hooks/use-hydrated';
import { useMediaQuery } from '../../../hooks/use-media-query';
import { usePlayableAtLocation } from '../../../hooks/use-playable-at-location';
import { findRoute } from '../../../models/PlayRoute';
import { getMediaAria } from '../../../utils/aria-helper';
import { dateOrWebFirstText } from '../../../utils/media-helper';
import { createSeparatedList } from '../../../utils/separated-list-helper';
import { useSearchDispatch } from '../SearchContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const MediaSearchResult = ({
  media,
  isDownloadFilterActive,
  order,
  resultIndex,
  searchTerm,
  previousPage
}) => {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const {
    videoDetailPath,
    videoShowPath
  } = usePath();
  const routes = useRoutes();
  const dispatch = useSearchDispatch();
  const analytics = useAnalyticsDispatch();
  const isPlayableAtLocation = usePlayableAtLocation(media);
  const isHydrated = useHydrated();
  const referrerPage = previousPage ? findRoute(previousPage, routes)?.pageId : undefined;
  const viewport = useMediaQuery([['small', {
    imageWidth: 140,
    showMediaType: false
  }], ['medium', {
    imageWidth: 250,
    showMediaType: true
  }]], {
    imageWidth: 300,
    showMediaType: true
  });
  const [isHovering, setHover] = useState(false);
  const subtitlesAvailable = media?.subtitlesAvailable;
  const openMedia = event => {
    const target = event.target;
    if (media.id && target.tagName && target.tagName.toLowerCase() !== 'a') {
      isVideo ? navigate(videoDetailPath(media), {
        state: {
          media
        }
      }) : window.location.pathname = videoDetailPath(media);
    }
    dispatch({
      type: 'add-search-entry',
      searchTerm
    });
    analytics({
      type: 'click-event',
      name: 'MediaSearchResultClick',
      value1: media.mediaType,
      value2: order
    });
    analytics({
      type: 'open-search-result',
      searchTerm,
      resultIndex: `${resultIndex}`,
      referrerPage
    });
  };
  const isVideo = media.mediaType === 'VIDEO';
  const list = [media.show && /*#__PURE__*/_jsx(ShowTitle, {
    to: videoShowPath(media.show),
    state: {
      show: media.show
    },
    "aria-hidden": "true",
    tabIndex: -1,
    isInternal: isVideo,
    children: media.show.title
  }), isHydrated && /*#__PURE__*/_jsx("time", {
    "aria-hidden": "true",
    children: `${dateOrWebFirstText(media.date, t)}`
  }, media.date)];
  const dotSeparatedList = createSeparatedList(list, /*#__PURE__*/_jsx(DotSeparator, {}));
  return /*#__PURE__*/_jsxs(StyledMediaSearchResult, {
    "data-testclass": "search-result",
    onClick: openMedia,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    children: [/*#__PURE__*/_jsxs(ImageContainer, {
      to: videoDetailPath(media),
      tabIndex: -1,
      "aria-hidden": "true",
      isInternal: true,
      children: [/*#__PURE__*/_jsx(PlayImage, {
        src: media.imageUrl,
        alt: media.title,
        aspectRatio: '16/9',
        width: 320,
        mediumImageWidth: 240,
        containerWidth: viewport.imageWidth
      }), !isPlayableAtLocation && /*#__PURE__*/_jsx(AbroadOverlay, {}), isPlayableAtLocation && /*#__PURE__*/_jsxs(Overlay, {
        children: [/*#__PURE__*/_jsx(SearchTeaserLabel, {
          label: getExpiringSoonLabelProps(media, 48, t)
        }), /*#__PURE__*/_jsx(MediaTypeOverlay, {
          children: /*#__PURE__*/_jsx(IconWrapper, {
            size: 45,
            icon: /*#__PURE__*/_jsx(MediaTypeIcon, {
              mediaType: media.mediaType,
              isHovering: isHovering,
              shadow: true
            })
          })
        }), /*#__PURE__*/_jsxs(MetaInformationContainer, {
          children: [subtitlesAvailable && /*#__PURE__*/_jsx(SubtitleLabel, {}), /*#__PURE__*/_jsx(MediaDuration, {
            media: media
          })]
        })]
      })]
    }), /*#__PURE__*/_jsxs(InfoContainer, {
      children: [dotSeparatedList.length > 0 && /*#__PURE__*/_jsx(DetailsContainer, {
        children: dotSeparatedList
      }), /*#__PURE__*/_jsx(Title, {
        to: videoDetailPath(media),
        "aria-label": isHydrated ? getMediaAria(media, t) : undefined,
        isInternal: true,
        children: media.title
      }), leadOrDescription(media) && /*#__PURE__*/_jsx(Description, {
        children: leadOrDescription(media, 300)
      }), /*#__PURE__*/_jsx(MetaInformation, {
        children: viewport.showMediaType && /*#__PURE__*/_jsx(Fragment, {
          children: isDownloadFilterActive && /*#__PURE__*/_jsx(Download, {
            colorSet: colorSets.transparent,
            size: "small",
            downloads: media
          })
        })
      })]
    })]
  });
};